import { Box, Button, Layout, Text } from '@wix/design-system'
import { ChoiceQuestionContentComponent, useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import { tryCatch } from '@wix/funnel-utils'
import React, { FC, useEffect, useState } from 'react'

import { usePageLogger } from '../../../bi/mfi'
import { delay } from '../../../utils/delay'
import { RichText } from '../../reusable/RichText'
import { WixMobileContentWrapper } from '../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileFooter } from '../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobilePage } from '../../reusable/WixMobilePage/WixMobilePage'
import { wixMobileSplitDataHooks } from './WixMobileSplit.dataHooks'
import s from './WixMobileSplit.scss'

export type Content = {
  title: string
  description: string
  isParagraphs: boolean
  question: ChoiceQuestionContentComponent<
    { title: string; subtitle: string },
    {
      label: string
    }
  >
  next: string
  nextMarkAsStudio: boolean
  nextUnmarkAsStudio: boolean
  skip: string
  skipMarkAsStudio: boolean
  skipUnmarkAsStudio: boolean
}

export interface WixMobileSplitProps {
  markAsWixStudioAccount: () => Promise<void>
  unmarkAsWixStudioAccount: () => Promise<void>
}

export const WixMobileSplit: FC<WixMobileSplitProps> = ({ markAsWixStudioAccount, unmarkAsWixStudioAccount }) => {
  const [isShowButtons, setIsShowButtons] = useState(false)
  const { content } = useContent<Content>()
  const pageLogger = usePageLogger()
  const [isPageLoading] = usePageLoading()
  const next = useNext()

  const handleSkip = async () => {
    const skipAnswer = 'wix_studio'
    if (isPageLoading) {
      return
    }

    // BI events must be tracked before markAsWixStudioAccount — KPI measures how many users who answer the design split are subsequently marked as Studio
    await tryCatch(() => pageLogger.generalAction({ action: skipAnswer }))
    await tryCatch(() => pageLogger.answer({ answer: skipAnswer }))
    if (content.skipMarkAsStudio) {
      await tryCatch(markAsWixStudioAccount)
    } else if (content.skipUnmarkAsStudio) {
      await tryCatch(unmarkAsWixStudioAccount)
    }
    next({ [content.question.questionKey]: { values: [skipAnswer] } })
  }

  const handleNext = async () => {
    const nextAnswer = 'wix_editor'
    if (isPageLoading) {
      return
    }

    // BI events must be tracked before markAsWixStudioAccount — KPI measures how many users who answer the design split are subsequently marked as Studio
    await tryCatch(() => pageLogger.generalAction({ action: nextAnswer }))
    await tryCatch(() => pageLogger.answer({ answer: nextAnswer }))
    if (content.nextMarkAsStudio) {
      await tryCatch(markAsWixStudioAccount)
    } else if (content.nextUnmarkAsStudio) {
      await tryCatch(unmarkAsWixStudioAccount)
    }
    next({ [content.question.questionKey]: { values: [nextAnswer] } })
  }

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  useEffect(() => {
    const showSkipWithDelay = async () => {
      await delay(100)
      setIsShowButtons(true)
    }
    showSkipWithDelay()
  }, [])

  return (
    <WixMobilePage>
      <WixMobileHeader />
      <div className={s.container}>
        <WixMobileContentWrapper>
          <WixMobileHeading dataHook={wixMobileSplitDataHooks.title()} textAlign="left">
            {content.title}
          </WixMobileHeading>

          <Layout cols={1} gap="8px">
            {content.description && (
              <div style={{ paddingTop: '12px' }}>
                <Text weight="normal" dataHook={wixMobileSplitDataHooks.description()}>
                  {content.description}
                </Text>
              </div>
            )}

            {content.isParagraphs ? (
              <Box direction="vertical" gap="24px" marginTop="20px">
                {content.question.options.map(option => (
                  <RichText
                    weight="thin"
                    id={option.optionKey}
                    dataHook={wixMobileSplitDataHooks.option({ key: option.optionKey })}
                  >
                    {option.label}
                  </RichText>
                ))}
              </Box>
            ) : (
              <ul className={s.list}>
                {content.question.options.map(option => (
                  <li key={option.label} className={s.listItem}>
                    <RichText
                      weight="normal"
                      id={option.label}
                      dataHook={wixMobileSplitDataHooks.option({ key: option.optionKey })}
                    >
                      {option.label}
                    </RichText>
                  </li>
                ))}
              </ul>
            )}
          </Layout>
        </WixMobileContentWrapper>

        <WixMobileFooter>
          <div className={s.buttons}>
            {content.next && isShowButtons && (
              <Button size="large" fullWidth onClick={handleNext} dataHook={wixMobileSplitDataHooks.next()}>
                {content.next}
              </Button>
            )}

            {content.skip && isShowButtons && (
              <Button
                id={wixMobileSplitDataHooks.skip()}
                priority="secondary"
                size="large"
                fullWidth
                onClick={handleSkip}
                dataHook={wixMobileSplitDataHooks.skip()}
              >
                {content.skip}
              </Button>
            )}
          </div>
        </WixMobileFooter>
      </div>
    </WixMobilePage>
  )
}
